/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/qrCodes.scss';
import {AwesomeIcon, Loader} from '@dvrd/dvr-controls';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {QrCodeProduct} from '../../util/interfaces';
import {BolmateButton, BolmateEmptyState, BolmateSearchInput} from '../controls';
import {DotsLoader} from '../controls/loader/dotsLoader';
import QrCodeCard from './qrCodeCard';

interface Props {
    onChangeSearch: (value: string) => void;
    onSearch: VoidFunction;
    onClickDownload: (product: QrCodeProduct) => VoidFunction;
    onClickDownloadAll: VoidFunction;
    onRowVisible: (ix: number) => VoidFunction;
    products: QrCodeProduct[];
    search: string;
    appliedSearch: string;
    loadingProducts: boolean;
    loadingMore: boolean;
    downloadingIds: Set<string>;
    downloadingAll: boolean;
}

export default function QrCodes(props: Props) {
    const {t} = useTranslation();
    const {
        onChangeSearch,
        onSearch,
        onClickDownload,
        onClickDownloadAll,
        onRowVisible,
        products,
        search,
        appliedSearch,
        loadingProducts,
        loadingMore,
        downloadingIds,
        downloadingAll
    } = props;

    function onSearchKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
        if (evt.key === 'Enter') onSearch();
    }

    function renderSearchContainer() {
        return (
            <div className='search-container'>
                <div className='left'>
                    <BolmateSearchInput
                        onChange={onChangeSearch}
                        value={search}
                        onKeyDown={onSearchKeyDown}
                        className='search-field'
                        placeholder={t('qrCodes.searchPlaceholder', 'Zoek op EAN of titel')}
                    />
                </div>
                <div className='right'>
                    <BolmateButton
                        onClick={onClickDownloadAll}
                        label={t('qrCodes.downloadAll.label', 'Download alle QR-codes')}
                        loading={downloadingAll}
                        disabled={!products.length && !appliedSearch.trim().length}
                        ornaments={{element: <AwesomeIcon name='qrcode' />}}
                        tertiary
                        shape='square'
                        align='center'
                    />
                </div>
            </div>
        );
    }

    function renderProducts() {
        return (
            <div className='qr-products'>
                <Loader active={loadingProducts} />
                {renderProductRows()}
            </div>
        );
    }

    function renderProductRows() {
        if (products.length) return <div className='qr-product-list'>{products.map(renderProduct)}</div>;
        if (loadingProducts) return null;
        if (appliedSearch.trim().length) return renderProductsNotFound();
        return renderEmptyProducts();
    }

    function renderProduct(product: QrCodeProduct, ix: number) {
        return (
            <QrCodeCard
                key={product.id}
                product={product}
                downloading={downloadingIds.has(product.id)}
                onClickDownload={onClickDownload(product)}
                onVisible={onRowVisible(ix)}
            />
        );
    }

    function renderProductsNotFound() {
        return (
            <BolmateEmptyState image='data-rows' title={t('qrCodes.notFound.title', 'Geen producten gevonden')}>
                {t('qrCodes.notFound.subtitle', 'Probeer een andere zoekterm of zoek op het volledige EAN.')}
            </BolmateEmptyState>
        );
    }

    function renderEmptyProducts() {
        return (
            <BolmateEmptyState image='data-rows' title={t('qrCodes.empty.title', 'Nog geen producten beschikbaar')}>
                {t(
                    'qrCodes.empty.subtitle',
                    'Zodra je actieve producten met een bol productpagina hebt, kun je hier hun QR-codes downloaden.'
                )}
            </BolmateEmptyState>
        );
    }

    return (
        <div className='qr-codes'>
            <div className='filter-container'>
                <div className='title-row'>
                    <label className='col-title'>{t('qrCodes.title', 'QR Codes')}</label>
                </div>
                <div className='page-description'>
                    <p>
                        {t(
                            'qrCodes.subtitle',
                            'Meer reviews verzamelen? Voeg nu QR-codes toe aan je productverpakkingen.'
                        )}
                    </p>
                    <p>
                        {t(
                            'qrCodes.subtitle2',
                            'Download per product de bijbehorende QR-code, print deze en voeg hem toe aan de verpakking. Elke QR-code leidt direct naar de juiste reviewpagina op bol en kan onbeperkt worden gescand.'
                        )}
                    </p>
                </div>
                {renderSearchContainer()}
            </div>
            {renderProducts()}
            {loadingMore && (
                <div className='load-more-container'>
                    <DotsLoader dotSize='3rem' />
                </div>
            )}
        </div>
    );
}
